import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { MoreHorizontal, Edit, Trash2, Download } from 'lucide-react';
import { AddAttachmentPayload, TicketEvent } from '@certhon/models';

interface EventActionsProps {
  event: TicketEvent;
  onEdit?: () => void;
  onDelete?: () => void;
  onDownload?: (attachment: AddAttachmentPayload) => void;
  canEdit?: boolean;
  canDelete?: boolean;
}

export function EventActions({
  event,
  onEdit,
  onDelete,
  onDownload,
  canEdit = false,
  canDelete = false,
}: EventActionsProps) {
  const getAvailableActions = () => {
    const actions = [];

    switch (event.type) {
      case 'ADD_MESSAGE':
        if (canEdit && onEdit) {
          actions.push({
            label: 'Edit',
            icon: Edit,
            onClick: onEdit,
            variant: 'default' as const,
          });
        }
        if (canDelete && onDelete) {
          actions.push({
            label: 'Delete',
            icon: Trash2,
            onClick: onDelete,
            variant: 'destructive' as const,
          });
        }
        break;

      case 'ADD_ATTACHMENT':
        if (onDownload) {
          actions.push({
            label: 'Download',
            icon: Download,
            onClick: () => onDownload(event.payload),
            variant: 'default' as const,
          });
        }
        if (canDelete && onDelete) {
          actions.push({
            label: 'Delete',
            icon: Trash2,
            onClick: onDelete,
            variant: 'destructive' as const,
          });
        }
        break;

      case 'ADD_LOCATION':
        if (canDelete && onDelete) {
          actions.push({
            label: 'Delete',
            icon: Trash2,
            onClick: onDelete,
            variant: 'destructive' as const,
          });
        }
        break;

      default:
        break;
    }

    return actions;
  };

  const actions = getAvailableActions();

  if (actions.length === 0) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground hover:text-foreground h-6 w-6 p-0 opacity-0
            transition-opacity group-hover/event:opacity-100 data-[state=open]:opacity-100
            pointer-coarse:opacity-100"
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-32"
        onCloseAutoFocus={(e) => {
          // we want to manage focus ourselves and move it to the Editor on edit
          e.preventDefault();
        }}
      >
        {actions.map((action, index) => (
          <DropdownMenuItem
            key={index}
            onClick={action.onClick}
            className={
              action.variant === 'destructive'
                ? 'text-red-600 focus:text-red-600'
                : ''
            }
          >
            <action.icon className="mr-2 h-4 w-4" />
            {action.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
